// skipcq: JS-W1028, JS-0128, JS-0417, JS-0418
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
// Firebase
import { doc, getDoc, updateDoc } from 'firebase/firestore';
// Utils
import { db } from '../../utils/firebase';
// Hooks
import useBrewery from '../../hooks/useBrewery';
import { applyCrawlerFailureDisposition } from '../../services/crawlerFailureDispositionService';
import {
  CRAWLER_FAILURE_DISPOSITION_ADOPT_SOCIAL,
  CRAWLER_FAILURE_DISPOSITION_ADOPT_WEBSITE,
  CRAWLER_FAILURE_DISPOSITION_FLAG_OOB,
} from '../../utils/crawlerFailureDispositionConstants';
import { getUserFacingCallableError } from '../../utils/callableErrorMessages';
import { isBlockedAsBreweryWebsiteUrl } from '../../utils/crawlerResearchWebsite';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Alert from '../../components/widgets/Alert';
import BreweryDeets from '../brewery/BreweryDeets';

const CrawlerFailureDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [failure, setFailure] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [applyingDisposition, setApplyingDisposition] = useState(false);
  const brewery = useBrewery(failure?.breweryId);

  // Get navigation data from location state
  const navigationData = location.state || {};
  const { filteredFailureIds = [], currentIndex = -1 } = navigationData;

  // Calculate if there are next/previous items available
  const hasPrevious = currentIndex > 0;
  const hasNext =
    currentIndex >= 0 && currentIndex < filteredFailureIds.length - 1;
  const previousId = hasPrevious ? filteredFailureIds[currentIndex - 1] : null;
  const nextId = hasNext ? filteredFailureIds[currentIndex + 1] : null;

  const formatWebsiteUrl = (url) => {
    if (!url) return '';
    // Add http:// if the URL doesn't start with http:// or https://
    return url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `https://${url}`;
  };

  useEffect(() => {
    const fetchFailure = async () => {
      try {
        const failureRef = doc(db, 'crawlerFailures', id);
        const failureDoc = await getDoc(failureRef);
        if (failureDoc.exists()) {
          const data = failureDoc.data();
          setFailure({
            id: failureDoc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
            lastRetry: data.lastRetry?.toDate() || null,
          });
        }
      } catch (error) {
        console.error('Error fetching failure:', error);
      }
    };

    fetchFailure();
  }, [id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'investigated':
        return 'info';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleNavigateBack = useCallback(() => {
    // Navigate back to list with preserved filter state
    if (navigationData?.filters) {
      const params = new URLSearchParams();
      const filters = navigationData.filters;

      // Safely add filter state to URL params
      if (filters.statusFilters) {
        if (filters.statusFilters.pending !== undefined) {
          params.set('pending', filters.statusFilters.pending.toString());
        }
        if (filters.statusFilters.investigated !== undefined) {
          params.set(
            'investigated',
            filters.statusFilters.investigated.toString(),
          );
        }
        if (filters.statusFilters.resolved !== undefined) {
          params.set('resolved', filters.statusFilters.resolved.toString());
        }
      }
      if (filters.errorLocationFilter) {
        params.set('location', filters.errorLocationFilter);
      }
      if (filters.orderBy) {
        params.set('orderBy', filters.orderBy);
      }
      if (filters.order) {
        params.set('order', filters.order);
      }

      navigate(`/failures?${params.toString()}`);
    } else {
      navigate('/failures');
    }
  }, [navigate, navigationData?.filters]);

  const handleNavigatePrevious = useCallback(() => {
    if (previousId) {
      navigate(`/failures/${previousId}`, {
        state: {
          ...navigationData,
          currentIndex: currentIndex - 1,
        },
      });
    }
  }, [navigate, previousId, navigationData, currentIndex]);

  const handleNavigateNext = useCallback(() => {
    if (nextId) {
      navigate(`/failures/${nextId}`, {
        state: {
          ...navigationData,
          currentIndex: currentIndex + 1,
        },
      });
    }
  }, [navigate, nextId, navigationData, currentIndex]);

  const buildAddressQuery = useCallback((breweryData) => {
    if (!breweryData) return '';

    const addressParts = [
      breweryData.name,
      breweryData.address1,
      breweryData.city,
      breweryData.state,
      breweryData.zip,
    ].filter(Boolean);

    return addressParts.join(' ');
  }, []);

  const handleSearchBrewery = useCallback(() => {
    if (brewery) {
      const searchQuery = buildAddressQuery(brewery);
      const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(
        searchQuery,
      )}`;
      window.open(googleSearchUrl, '_blank', 'noopener,noreferrer');
    }
  }, [brewery, buildAddressQuery]);

  const handleMapBrewery = useCallback(() => {
    if (brewery) {
      const locationQuery = buildAddressQuery(brewery);
      const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(
        locationQuery,
      )}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  }, [brewery, buildAddressQuery]);

  const handleNavigateToBrewery = useCallback(() => {
    if (brewery) {
      navigate(`/brewery/${brewery.id}`);
    }
  }, [navigate, brewery]);

  const handleSetToPending = useCallback(async () => {
    try {
      const failureRef = doc(db, 'crawlerFailures', id);
      await updateDoc(failureRef, {
        status: 'pending',
      });

      // Update local state
      setFailure((prev) => ({
        ...prev,
        status: 'pending',
      }));
    } catch (error) {
      console.error('Error updating failure status:', error);
    }
  }, [id]);

  const handleSetToResolved = useCallback(async () => {
    try {
      const failureRef = doc(db, 'crawlerFailures', id);
      await updateDoc(failureRef, {
        status: 'resolved',
      });

      // Update local state
      setFailure((prev) => ({
        ...prev,
        status: 'resolved',
      }));
    } catch (error) {
      console.error('Error updating failure status:', error);
    }
  }, [id]);

  const applyResearchDisposition = useCallback(
    async (disposition, successText) => {
      if (!id) {
        const errorMsg = 'Missing failure ID';
        setErrorMessage(errorMsg);
        setErrorOpen(true);
        return;
      }
      if (
        disposition !== CRAWLER_FAILURE_DISPOSITION_FLAG_OOB
        && !failure?.research?.website
      ) {
        const errorMsg = 'Missing required data for this action';
        setErrorMessage(errorMsg);
        setErrorOpen(true);
        return;
      }
      if (
        disposition === CRAWLER_FAILURE_DISPOSITION_FLAG_OOB
        && !failure?.breweryId
      ) {
        const errorMsg = 'Missing brewery ID for flagging out of business';
        setErrorMessage(errorMsg);
        setErrorOpen(true);
        return;
      }

      setApplyingDisposition(true);
      try {
        await applyCrawlerFailureDisposition({
          failureId: id,
          disposition,
          website: failure?.research?.website,
        });
        setFailure((previousFailure) => ({
          ...previousFailure,
          status: 'resolved',
        }));
        setSuccessMessage(successText);
        setSuccessOpen(true);
      } catch (dispositionError) {
        console.error(
          'Error applying crawler failure disposition:',
          dispositionError,
        );
        setErrorMessage(
          getUserFacingCallableError(dispositionError)
            || 'Failed to apply disposition',
        );
        setErrorOpen(true);
      } finally {
        setApplyingDisposition(false);
      }
    },
    [id, failure],
  );

  const handleAdoptWebsite = useCallback(async () => {
    await applyResearchDisposition(
      CRAWLER_FAILURE_DISPOSITION_ADOPT_WEBSITE,
      'Website adopted successfully!',
    );
  }, [applyResearchDisposition]);

  const handleAdoptSocial = useCallback(async () => {
    await applyResearchDisposition(
      CRAWLER_FAILURE_DISPOSITION_ADOPT_SOCIAL,
      'Social profile adopted successfully!',
    );
  }, [applyResearchDisposition]);

  const handleFlagOutOfBusiness = useCallback(async () => {
    await applyResearchDisposition(
      CRAWLER_FAILURE_DISPOSITION_FLAG_OOB,
      'Brewery flagged as out of business successfully!',
    );
  }, [applyResearchDisposition]);

  const handleCloseSnackbar = useCallback(() => {
    setSuccessOpen(false);
    setSuccessMessage('');
  }, []);

  const handleCloseErrorSnackbar = useCallback(() => {
    setErrorOpen(false);
    setErrorMessage('');
  }, []);

  const formatRecommendation = (recommendation) => {
    switch (recommendation) {
      case 'manual_review':
        return 'Manual Review';
      case 'adopt_website':
        return 'Adopt Website';
      case 'flag_oob':
        return 'Flag Out of Business';
      default:
        return recommendation;
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'manual_review':
        return 'warning';
      case 'adopt_website':
        return 'success';
      case 'flag_oob':
        return 'error';
      default:
        return 'default';
    }
  };

  const ConfidenceBar = ({ confidence }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {confidence}%
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: 200,
          height: 8,
          bgcolor: 'grey.200',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${confidence}%`,
            height: '100%',
            bgcolor:
              confidence > 70
                ? 'success.main'
                : confidence > 40
                ? 'warning.main'
                : 'error.main',
          }}
        />
      </Box>
    </Box>
  );

  // Common props for navigation buttons
  const navButtonProps = {
    variant: 'outlined',
    color: 'secondary',
    size: 'small',
  };

  const NavigationButtons = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {brewery && (
        <>
          <Button
            startIcon={<MapIcon />}
            onClick={handleMapBrewery}
            {...navButtonProps}
          >
            Map
          </Button>
          <Button
            startIcon={<SearchIcon />}
            onClick={handleSearchBrewery}
            {...navButtonProps}
          >
            Search
          </Button>
          <Button
            startIcon={<StoreIcon />}
            onClick={handleNavigateToBrewery}
            {...navButtonProps}
          >
            Brewery
          </Button>
        </>
      )}
      {(hasPrevious || hasNext) && (
        <>
          <Button
            startIcon={<ArrowBackIosIcon />}
            onClick={handleNavigatePrevious}
            disabled={!hasPrevious}
            {...navButtonProps}
          >
            Previous
          </Button>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={handleNavigateNext}
            disabled={!hasNext}
            {...navButtonProps}
          >
            Next
          </Button>
        </>
      )}
    </Box>
  );

  const researchWebsite = failure?.research?.website;
  const researchWebsiteIsSocial = isBlockedAsBreweryWebsiteUrl(researchWebsite);
  const dispositionButtonsDisabled =
    applyingDisposition || failure?.status === 'resolved';

  const ActionButtons = () => (
    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
      <Button
        variant="contained"
        color="warning"
        disabled={applyingDisposition || failure.status === 'pending'}
        onClick={handleSetToPending}
      >
        Set to Pending
      </Button>
      <Button
        variant="contained"
        color="success"
        disabled={applyingDisposition || failure.status === 'resolved'}
        onClick={handleSetToResolved}
      >
        Set to Resolved
      </Button>
      {researchWebsite && !researchWebsiteIsSocial && (
        <Button
          variant="contained"
          color="success"
          onClick={handleAdoptWebsite}
          disabled={
            dispositionButtonsDisabled
            || brewery?.website === researchWebsite
          }
        >
          {applyingDisposition ? 'Adopting...' : 'Adopt Website'}
        </Button>
      )}
      {researchWebsite && researchWebsiteIsSocial && (
        <Button
          variant="contained"
          color="success"
          onClick={handleAdoptSocial}
          disabled={dispositionButtonsDisabled}
        >
          {applyingDisposition ? 'Adopting...' : 'Adopt Social'}
        </Button>
      )}
      {!brewery?.isOob && (
        <Button
          variant="contained"
          color="error"
          onClick={handleFlagOutOfBusiness}
          disabled={dispositionButtonsDisabled}
        >
          {applyingDisposition ? 'Flagging...' : 'Flag Out of Business'}
        </Button>
      )}
    </Box>
  );

  const ErrorInformation = () => (
    <Grid item xs={12} md={6}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Error Information
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Timestamp: {failure.timestamp.toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Error Type: {failure.errorType}
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Location: {failure.errorLocation}
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Retry Count: {failure.retryCount}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Error Message
        </Typography>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            bgcolor: 'grey.100',
            p: 2,
            borderRadius: 1,
          }}
        >
          {failure.errorMessage}
        </Typography>
      </Box>

      {failure.errorStack && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Error Stack
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
            }}
          >
            {failure.errorStack}
          </Typography>
        </Box>
      )}

      {failure.notes && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2">{failure.notes}</Typography>
          </Box>
        </>
      )}
    </Grid>
  );

  const renderResearchResults = () => {
    if (!failure?.research) return null;

    const {
      website,
      confidence,
      justification,
      references,
      out_of_business_confidence,
      out_of_business_justification,
      recommendation,
    } = failure.research;

    return (
      <>
        <Typography variant="h5" sx={{ mb: 2 }}>
          AI Research Results
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <List>
              {website && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Website"
                    secondary={
                      <Typography
                        component="a"
                        href={formatWebsiteUrl(website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="tertiary"
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': { color: 'tertiary.dark' },
                        }}
                      >
                        {website}
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {confidence !== undefined && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Website Confidence Score"
                    secondary={<ConfidenceBar confidence={confidence} />}
                  />
                </ListItem>
              )}
              {justification && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Website Justification"
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {justification}
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              {references && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="References"
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {references}
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Box>

          {(out_of_business_confidence !== undefined ||
            out_of_business_justification ||
            recommendation) && (
            <Box sx={{ flex: 1 }}>
              <List>
                {out_of_business_confidence !== undefined && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Out of Business Confidence"
                      secondary={
                        <ConfidenceBar
                          confidence={out_of_business_confidence}
                        />
                      }
                    />
                  </ListItem>
                )}
                {out_of_business_justification && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Out of Business Justification"
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {out_of_business_justification}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                {recommendation && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="AI Recommendation"
                      secondary={
                        <Chip
                          label={formatRecommendation(recommendation)}
                          color={getRecommendationColor(recommendation)}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    // skipcq: JS-0415
    <DashboardLayout title="Failure Details">
      {!failure ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh"
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleNavigateBack}
              color="secondary"
            >
              Back to Data Issues
            </Button>
            <NavigationButtons />
          </Box>

          {failure.research && (
            <Paper sx={{ p: 3, mb: 3 }}>{renderResearchResults()}</Paper>
          )}

          <ActionButtons />

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h5">Failure Details</Typography>
              <Chip
                label={failure.status}
                color={getStatusColor(failure.status)}
              />
            </Box>

            <Grid container spacing={3}>
              <ErrorInformation />
              <Grid item xs={12} md={6}>
                {brewery && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Brewery Information
                    </Typography>
                    <BreweryDeets
                      name={brewery.name}
                      address1={brewery.address1}
                      city={brewery.city}
                      state={brewery.state}
                      zip={brewery.zip}
                      phone={brewery.phone}
                      website={brewery.website}
                      isOob={brewery.isOob}
                      oobDate={brewery.oobDate}
                      email={brewery.email}
                      socials={brewery.socials}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Snackbar
            open={successOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              sx={{ width: '100%' }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          <Snackbar
            open={errorOpen}
            autoHideDuration={8000}
            onClose={handleCloseErrorSnackbar}
          >
            <Alert
              onClose={handleCloseErrorSnackbar}
              severity="error"
              sx={{ width: '100%' }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </DashboardLayout>
  );
};

export default CrawlerFailureDetailsPage;
